import React, { useRef } from 'react';
import { motion, useInView } from "motion/react";
import { ArrowRightIcon } from './icons/ClubPageIcons';
import { getSignedMediaUrl } from '@/lib/utils';

interface CTACardProps {
  title: string;
  body: string;
  ctaText: string;
  ctaLink: string;
  backgroundImage: string | null;
  className?: string;
}

const CTACard: React.FC<CTACardProps> = ({
  title,
  body,
  ctaText,
  ctaLink,
  backgroundImage,
  className = ''
}) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.5 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" as const } }
  };

  return (
    <section ref={sectionRef} className={`relative w-full h-[50vh] flex items-center justify-center ${className}`}>
      {/* Single background image spanning full width */}
      <img
        src={getSignedMediaUrl(backgroundImage, 'image').url}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          // Show fallback if image fails to load
          const fallback = target.nextElementSibling as HTMLElement;
          if (fallback) fallback.style.display = 'flex';
        }}
      />
      {/* Fallback placeholder if image fails to load */}
      <div className="hidden items-center justify-center bg-gray-800 text-gray-500 w-full h-full">
        <span className="text-sm tracking-widest uppercase">{backgroundImage}</span>
      </div>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/70"></div>
      
      {/* Content overlaid on top */}
      <motion.div 
        className="relative z-10 max-w-4xl mx-auto px-8 text-center flex flex-col items-center"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <motion.h2 variants={itemVariants} className="text-5xl md:text-6xl font-serif font-medium text-white mb-6">
          {title}
        </motion.h2>
        <motion.p variants={itemVariants} className="text-lg text-gray-300 leading-relaxed mb-10">
          {body}
        </motion.p>
        <motion.div variants={itemVariants}>
          <a href={ctaLink} className="group inline-flex items-center justify-center px-10 py-5 bg-blue-600 text-white rounded-full font-medium text-lg transition-all duration-300 hover:bg-blue-500 transform hover:scale-105">
            <span className="flex items-center gap-2">{ctaText} <ArrowRightIcon /></span>
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default CTACard;
